// Third Party
import React, { useEffect } from 'react';

// Namespace
import * as ProposalNS from '../../../Proposal';

// Utils
import { getMaxNumberOfItems, getTabElements } from '@/modules/proposal/ProposalHelpers';
import { formatMessage } from '@/utils/i18n';

// Types
import User from '@/types/user';
import LocalizedString from '@/types/localized-string';
import Comparison from '@/types/comparison';

// Components
import CollapsibleSection from './CollapsibleSection';
import ProposalDivision from '../proposal-content-selection/ProposalDivision';

// Utils
import { some } from 'lodash';

import ProposalItemSelector from '../../proposal-sidebar/components/ProposalItemSelector';
import { ProposalSidebarOptions } from '../../proposal-sidebar/ProposalSidebar';
import ProposalItemLister from '../../proposal-sidebar/components/proposal-item-lister/ProposalItemLister';
import Locale from '@/types/locale';
import { useProposalActions } from '../../preview/ProposalActionsContext';

type CustomizeSidebarProps = {
  sidebar: ProposalNS.ProposalSidebarTab;
  user: User;
  template: ProposalNS.ProposalTemplateV2;
  proposal: ProposalNS.Proposal;
  comparison: Comparison;
  locale: Locale;
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions;
  gifHideBaseScenario: boolean | undefined;
};

const CustomizeSidebar = ({ sidebar, user, template, proposal, comparison, proposalTemplateOptions, locale, gifHideBaseScenario }: CustomizeSidebarProps) => {
  const { styleOptions, sections: selectedSections, hiddenLayouts, divisions: selectedDivisions } = proposalTemplateOptions;
  const elements = getTabElements(sidebar, user);
  const [selectedSectionsLocalState, setSelectedSectionsLocalState] = React.useState(new Map<string, boolean>());
  const {
    onItemsSelection,
    onItemPurposeFlagUpdate,
    onGifItemsSelection,
    onToggleGifBaseScenario,
    onToggleFundProfiles,
    onToggleGroupProfiles,
    onTogglePortfolioProfiles,
    onToggleDivision,
    onToggleHiddenLayout,
    onToggleSection,
    onToggleShortHistory,
    onChangeItemOrder,
  } = useProposalActions();

  const sectionResults: LocalizedString[] = proposal.template.root.reduce((acc, division) => {
    if (division.label != null && some(Object.values(division.label))) {
      acc.push(division.label);
    }
    division.sections.forEach(section => {
      if (section.label != null && some(Object.values(section.label))) {
        acc.push(section.label);
      }
    });
    return acc;
  }, [] as Array<LocalizedString>);

  useEffect(() => {
    selectedSectionsLocalState.keys().forEach(key => {
      const sectionHasBeenLoadedInPreview = selectedSectionsLocalState.get(key) === true && selectedSections.includes(key);
      const sectionHasBeenRemovedOffPreview = selectedSectionsLocalState.get(key) === false && !selectedSections.includes(key);
      const mustResetLoadingState = sectionHasBeenLoadedInPreview || sectionHasBeenRemovedOffPreview;

      if (mustResetLoadingState) {
        selectedSectionsLocalState.delete(key);
      }
    });
    setSelectedSectionsLocalState(new Map(selectedSectionsLocalState));

    // Avoid an infinite loop of updating localShowSectionsCache
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSections]);

  const renderElement = (element: ProposalNS.ProposalSidebarElement, index: number) => {
    const {
      items,
      template: { customParameters, itemManagement },
    } = proposal;
    const { allowedItemTypes } = itemManagement;
    const { orientation } = styleOptions;
    const maxItems: number = getMaxNumberOfItems(template, orientation);
    const { itemSelectors } = element as ProposalNS.ProposalItemSelection;

    switch (element.type) {
      case ProposalSidebarOptions.ITEM_SELECTOR:
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.Tabs.ItemSelector.Title')}>
            <ProposalItemSelector
              onSelection={onItemsSelection}
              comparisonItems={comparison.items}
              selectedProposalItems={items}
              numberOfControls={maxItems}
              itemSelectors={itemSelectors}
              singleItemShift={customParameters && customParameters.singleItemShift}
              allowedItemTypes={allowedItemTypes}
              showTitle={false}
            />
          </CollapsibleSection>
        );
      case ProposalSidebarOptions.ITEM_LISTER:
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.ItemSelection')}>
            <div className="proposal-item-lister">
              <ProposalItemLister
                user={user}
                locale={locale}
                onItemsSelection={onItemsSelection}
                onItemPurposeFlagUpdate={onItemPurposeFlagUpdate}
                onGifItemsSelection={onGifItemsSelection}
                onToggleGifBaseScenario={onToggleGifBaseScenario}
                onToggleFundProfiles={onToggleFundProfiles}
                onTogglePortfolioProfiles={onTogglePortfolioProfiles}
                onToggleGroupProfiles={onToggleGroupProfiles}
                onToggleShortHistory={onToggleShortHistory}
                proposal={proposal}
                comparison={comparison}
                allowedItemTypes={allowedItemTypes}
                maxItemsNumber={maxItems}
                gifHideBaseScenario={gifHideBaseScenario}
                showIncludeFundProfiles={element.showIncludeFundProfiles}
                showIncludePortfolioProfiles={element.showIncludePortfolioProfiles}
                showIncludeGroupProfiles={element.showIncludeGroupProfiles}
                showShortHistory={element.showShortHistory}
                showProposalItemPurposeSelector={element.showProposalItemPurposeSelector}
                itemColorOverride={element.itemColorOverride}
                onChangeItemOrder={onChangeItemOrder}
              />
            </div>
          </CollapsibleSection>
        );
      case ProposalSidebarOptions.PROPOSAL_SECTIONS:
        return (
          <CollapsibleSection key={index} title={formatMessage('Proposal.Tabs.Customize.Title')}>
            <div className="edit-proposal-sections">
              {template.root.slice(0).map(division => (
                <ProposalDivision
                  key={division.key}
                  division={division}
                  proposal={proposal}
                  user={user}
                  selectedSections={selectedSections}
                  selectedDivisions={selectedDivisions}
                  filterQuery=""
                  results={sectionResults}
                  onToggleDivision={onToggleDivision}
                  onToggleSection={onToggleSection}
                  onToggleHiddenLayout={onToggleHiddenLayout}
                  hiddenLayouts={hiddenLayouts}
                  selectedSectionsLocalState={selectedSectionsLocalState}
                  setSelectedSectionsLocalState={setSelectedSectionsLocalState}
                  showLoaders={comparison.items.reduce((acc, item) => acc + item.holdings.length, 0) > 100}
                />
              ))}
            </div>
          </CollapsibleSection>
        );

      default:
        return null;
    }
  };

  return <div>{elements.map((element, index) => renderElement(element, index))}</div>;
};

export default CustomizeSidebar;
